import { useEffect, useState } from "react";
import {
  BoxedTable,
  Button,
  Input,
  Modal,
  PageContainer,
} from "../../../components";
import { professoresApi } from "../../../services";
import dayjs from "dayjs";
import { useForm, useModal } from "../../../hooks";
import Form from "../../../components/form/Form";
import Storage from "../../../helpers/Storage";
import { AlunoPresenca } from "../../../models";
import Alert from "../../../helpers/Alert";
import { FaCheck } from "react-icons/fa";
import { tw } from "@matheuscaetano/helprs";
import { BiSearchAlt2 } from "react-icons/bi";

type RowItem = {
  dia_semana: string;
  data: string;
  horario: string;
  aluno_curso_id: number;
  aluno_id: number;
  professor_id: number;
  order?: number;
  // aulas: {
  aluno_nome: string;
  presenca?: null | AlunoPresenca;
  // }[];
};

export default function Presencas() {
  const { open: openModal, close: closeModal, modalProps } = useModal();
  const [saving, setSaving] = useState(false);
  const [modalData, setModalData] = useState<RowItem>({} as any);

  const filter = useForm({
    initialData: {
      data_referencia: dayjs().format("YYYY-MM-DD"),
    },
  });

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RowItem[]>([]);

  const formattedData = data.map((item) => {
    item.dia_semana = dayjs(item.data)
      .locale("pt-br")
      .format("ddd")
      .toUpperCase();

    return item;
  });

  let highestAulaIndex = 0;

  // should not have aluno_curso_id duplicated in the table
  let uniqueAlunoCursoIds = new Set<number>();
  const filteredData = formattedData
    .filter((item) => {
      if (uniqueAlunoCursoIds.has(item.aluno_curso_id)) {
        return false; // Skip this item if the aluno_curso_id is already present
      }
      uniqueAlunoCursoIds.add(item.aluno_curso_id);
      return true; // Keep this item if the aluno_curso_id is unique
    })
    .map((filteredItem) => {
      formattedData
        .filter((item) => item.aluno_curso_id === filteredItem.aluno_curso_id)
        .forEach((item, i) => {
          if (i > highestAulaIndex) highestAulaIndex = i;
          filteredItem = {
            ...filteredItem,
            [`aula_${i}`]: item.presenca || {
              aluno_curso_id: item.aluno_curso_id,
              aluno_id: item.aluno_id,
              professor_id: item.professor_id,
              aula: i + 1,
              presente: true,
              observacao: "",
              data:
                formattedData.find(
                  (formattedItem) =>
                    formattedItem.aluno_curso_id === item.aluno_curso_id &&
                    formattedItem.order === i
                )?.data || "",
              inicio_aula: item.horario.split("até")[0].trim(),
              final_aula: item.horario.split("até")[1].trim(),
            },
          };
        });

      return filteredItem;
    });

  const tableColumns = [
    {
      title: "DIA",
      key: "dia_semana",
      columnGroupClassName: "max-w-[50px]",
    },
    {
      title: "HORÁRIO",
      key: "horario",
      columnGroupClassName: "max-w-[150px]",
    },
    {
      title: "ALUNO",
      key: "aluno_nome",
    },
    {
      title: "",
      columnGroupClassName: "max-w-[35px]",
      render: (item: any) => <BiSearchAlt2 size={24}  onClick={() => window.location.href = `/p/alunos?aluno_id=${item.aluno_id}`} className="cursor-pointer" />
    },
    ...Array.from({ length: highestAulaIndex + 1 }).map((_, i) => {
      const index = i + 1;
      return {
        title: index < 10 ? `0${index}` : `${index}`,
        key: `aula_${index}`,
        columnGroupClassName: "max-w-[35px]",
        render: (item: RowItem) => {
          const itemPresenca = (item as any)[`aula_${index - 1}`] as AlunoPresenca;
          return (
            <button
              onClick={() => {
                setModalData({
                  ...item,
                  presenca: itemPresenca,
                });
                openModal();
              }}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {itemPresenca?.id ? (
                !itemPresenca.presente ? (
                  <span className="text-red-500">❌</span>
                ) : (
                  <span className="text-green-500">
                    <FaCheck color="green" className="mt-1" />
                  </span>
                )
              ) : (
                <div style={{ opacity: "0" }}>o</div>
              )}
            </button>
          );
        },
      } as any;
    }),
  ];

  async function load() {
    setLoading(true);
    const { data, isError } = await professoresApi.getPresencas({
      professor_id: Storage.getUserData()!.usuario.id!,
      ...filter.form,
    });

    if (isError) {
      Alert.error(data.message || data.error || "Erro ao carregar presenças");
      return;
    }

    setLoading(false);
    setData(data ?? []);
  }

  async function savePresenca() {
    setSaving(true);
    const { data, isError } = await professoresApi.setAlunoPresenca(modalData);
    load();
    setSaving(false);
    if (isError) {
      Alert.error(data.message || data.error || "Erro ao salvar presença");
      return;
    }
    closeModal();
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer pageTitle="Controle de Presenças">
      <div className={"flex flex-col"}>
        <div className={"flex flex-row justify-between p-2 w-full"}>
          <h1 className={"text-3xl flex items-center"}>
            Controle de Presenças
          </h1>
        </div>
      </div>

      <div className="my-10">
        <Form className="flex justify-between" onSubmit={load}>
          <div>
            <Input
              label="Data de referência"
              type="date"
              {...filter.register("data_referencia")}
            />
          </div>

          <Button
            className="bg-constancia text-white hover:bg-constancia/80 mt-4"
            type="submit"
          >
            Pesquisar
          </Button>
        </Form>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <span>Carregando...</span>
        </div>
      ) : !data.length ? (
        <div className="flex justify-center items-center h-64">
          <span>Nenhuma aula para esta data</span>
        </div>
      ) : (
        <main>
          <BoxedTable
            className="mt-8"
            items={filteredData}
            columns={tableColumns}
          />

          <Modal {...modalProps} title={modalData?.aluno_nome} size="lg">
            <div className="flex items-center gap-4">
              <button
                onClick={() =>
                  setModalData({
                    ...modalData,
                    presenca: { ...modalData.presenca, presente: false } as any,
                  })
                }
                className={tw(
                  "border rounded h-[60px] w-[60px] text-white text-[30px] flex items-center justify-center hover:opacity-70",
                  !modalData.presenca?.presente ? "bg-blue-300" : ""
                )}
              >
                ❌
              </button>
              <button
                onClick={() =>
                  setModalData({
                    ...modalData,
                    presenca: { ...modalData.presenca, presente: true } as any,
                  })
                }
                className={tw(
                  "border rounded h-[60px] w-[60px] text-white text-[30px] flex items-center justify-center hover:opacity-70",
                  modalData.presenca?.presente ? "bg-blue-300" : ""
                )}
              >
                <FaCheck color="green" />
              </button>
            </div>

            <br />

            <Input label="Data" type="date" value={modalData?.presenca?.data} disabled />

            <div className="mt-2">
              <Input
                label="Observação"
                value={modalData?.presenca?.observacao}
                setValue={(observacao) =>
                  setModalData({
                    ...modalData,
                    presenca: { ...modalData.presenca, observacao } as any,
                  })
                }
              />
            </div>

            <footer className="flex justify-end mt-10">
              <Button
                type="button"
                variant="constancia"
                loading={saving}
                onClick={savePresenca}
              >
                Salvar
              </Button>
            </footer>
          </Modal>
        </main>
      )}
    </PageContainer>
  );
}
